import * as React from "react";
import styled, { css } from "styled-components";

import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

export interface NotificationBannerProps {
  id: string;
  type: "error" | "notice";
  show: boolean;
  message: string;
  button?: NotificationBannerButtonProps;
  duration?: number;
  dismissible?: boolean;
  onClose?: () => void;
}

interface NotificationBannerButtonProps {
  label: string;
  onClick: () => void;
}

interface StyledNotificationBannerProps {
  height: number;
  animate: boolean;
  type: "error" | "notice";
}

/**
 * Notification banner
 */
const NotificationBanner: React.ReactFCWithChildren<NotificationBannerProps> = ({
  dismissible,
  show,
  message,
  button,
  onClose,
  type
}) => {
  const bannerHeightPx = 55;
  const [ animate, setAnimate ] = React.useState(false);

  // On mount
  React.useEffect(() => {
    // Update the animate state
    setAnimate(show);
  }, [ show ]);

  return (
    <StyledNotificationBanner
      className="notify-banner"
      height={bannerHeightPx}
      animate={animate}
      type={type}
    >
      <div className="notify-banner-wrapper">
        <Text
          className="copy"
          fontSize="h4"
        >
          {message}
        </Text>

        {button && (
          <Button
            className="btn"
            size="micro"
            onClick={button.onClick}
            $buttonType="secondary"
          >
            {button.label}
          </Button>
        )}
      </div>

      {dismissible && (
        <Icon
          className="close"
          icon={Icons.CLOSE}
          $fillColor="primaryLight"
          size={24}
          onClick={onClose}
        />
      )}
    </StyledNotificationBanner>
  );
};

// Default props
NotificationBanner.defaultProps = { dismissible: true };

export default NotificationBanner;

const StyledNotificationBanner = styled.li<StyledNotificationBannerProps>`
  ${({
    theme, height, animate, type
  }) => css`
    transition: ${theme.transition.quick};
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    min-height: ${animate ? `${height / 10}rem` : 0};
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
    background-color: ${type === "error" ? theme.colors.error : theme.colors.primaryLight};
    overflow: hidden;

    .notify-banner-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 auto;
      padding-left: ${theme.spacing.padding.md};
      padding-right: 5.4rem;
      height: 100%;

      @media (min-width: 768px) {
        padding-right: ${theme.spacing.padding.md};
      }

      .copy {
        color: ${theme.colors.white};
      }

      .btn {
        margin-left: 1.2rem;
      }
    }

    .close {
      position: absolute;
      right: ${theme.spacing.padding.md};
    }
  `};
`;
